import React from 'react'
import { Heading, Rate, Text } from '@afterpaytouch/core'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import { BASE_PATH } from '../../env'
import styles from './style.module.css'
import { useExperiment } from '../../state'
import { isIOS, isAfterpayInAppBrowser } from '../../utils/device'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { useSocialBannerEligibility, useSocialProofExperiment } from './hooks'

export const SocialBanner = (): JSX.Element | null => {
  const { t } = useTranslation()
  const socialProofExperiment = useSocialProofExperiment()
  const isAppSession = isAfterpayInAppBrowser()
  const experiment = useExperiment('CheckoutSocialProofExperiment', !isAppSession, socialProofExperiment)
  const isSocialBannerEligible = useSocialBannerEligibility()
  const { logEvent } = useMonitoringWithEnduringEventProperties()

  const is_iOS = isIOS()

  function getValueByOS<T>(iOSVal: T, androidVal: T): T {
    return is_iOS ? iOSVal : androidVal
  }

  const data = {
    A: {
      imgSrc: `${BASE_PATH}/images/${t('login:socialBanner_A.imgSrc')}`,
      heading: t('login:socialBanner_A.heading'),
      subHeading: (
        <Text size='XS' color='Gray10'>
          {t('login:socialBanner_A.subHeading')}
        </Text>
      ),
      tipsText: t('login:socialBanner_A.tips'),
    },
    B: {
      imgSrc: getValueByOS(`${BASE_PATH}/images/${t('login:socialBanner_B.imgSrc_ios')}`, `${BASE_PATH}/images/${t('login:socialBanner_B.imgSrc_android')}`),
      heading: getValueByOS(t('login:socialBanner_B.heading_ios'), t('login:socialBanner_B.heading_android')),
      subHeading: (
        <>
          <Rate size='Small' value={Number(getValueByOS(t('login:socialBanner_B.rate_ios'), t('login:socialBanner_B.rate_android')))} />
          <span className={styles.subHeadingTextWrapper}>
            <Text size='XS' renderAs='span' color='Gray10'>
              {getValueByOS(t('login:socialBanner_B.subHeading_ios'), t('login:socialBanner_B.subHeading_android'))}
            </Text>
          </span>
        </>
      ),
      tipsText: getValueByOS(t('login:socialBanner_B.tips_ios'), t('login:socialBanner_B.tips_android')),
    },
  }

  const handleBannerOnClick = (): void => {
    logEvent(TrackingEvent.PRESSED_SOCIAL_BANNER)
  }

  if (!isSocialBannerEligible) {
    return null
  }

  return (
    <div className={styles.container}>
      <div className={styles.banner} onClick={handleBannerOnClick} data-testid='social-banner'>
        <div className={styles.left}>
          <Heading size='M' testNameSpace='social-banner'>
            {data[experiment.value].heading}
          </Heading>
          {data[experiment.value].subHeading}
          <div className={styles.tipsContainer}>
            <Text size='XXS' color='Gray50' testNameSpace='social-banner-tips' fontFace='CashSansMono'>
              {data[experiment.value].tipsText}
            </Text>
          </div>
        </div>
        <div className={styles.right}>
          <Image
            src={data[experiment.value].imgSrc}
            alt=''
            width={experiment.value === 'A' ? 123 : 88}
            height={81}
            style={{
              maxWidth: '100%',
              height: 'auto',
            }}
          />
        </div>
      </div>
    </div>
  )
}
